import React, { useState, useEffect, useRef } from 'react';
import './LazyImage.css';

const LazyImage = ({
  src,
  alt,
  className = '',
  placeholder = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 300"%3E%3Crect fill="%23f3f4f6" width="400" height="300"/%3E%3C/svg%3E',
  onLoad,
  onError,
  ...props
}) => {
  const [imageSrc, setImageSrc] = useState(placeholder);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    let observer;
    let didCancel = false;

    if (imgRef.current && 'IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!didCancel && (entry.isIntersecting || entry.intersectionRatio > 0)) {
              const image = new Image();
              image.src = src;

              image.onload = () => {
                if (!didCancel) {
                  setImageSrc(src);
                  setImageLoaded(true);
                  if (onLoad) onLoad();
                }
              };

              image.onerror = () => {
                if (!didCancel) {
                  setImageError(true);
                  if (onError) onError();
                }
              };

              if (observer && imgRef.current) {
                observer.unobserve(imgRef.current);
              }
            }
          });
        },
        {
          rootMargin: '50px',
          threshold: 0.01,
        }
      );

      observer.observe(imgRef.current);
    } else {
      // Fallback para navegadores sin soporte de IntersectionObserver
      setImageSrc(src);
    }

    return () => {
      didCancel = true;
      if (observer && observer.disconnect) {
        observer.disconnect();
      }
    };
  }, [src, onLoad, onError]);

  if (imageError) {
    return (
      <div className={`lazy-image lazy-image--error ${className}`} ref={imgRef} role="img" aria-label={alt}>
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="lazy-image__error-icon"
        >
          <rect width="24" height="24" fill="#f3f4f6" />
          <path
            d="M21 19V5C21 3.9 20.1 3 19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19ZM8.5 13.5L11 16.51L14.5 12L19 18H5L8.5 13.5Z"
            fill="#9CA3AF"
          />
        </svg>
        <span className="sr-only">{alt}</span>
      </div>
    );
  }

  return (
    <img
      ref={imgRef}
      src={imageSrc}
      alt={alt}
      className={`lazy-image ${imageLoaded ? 'lazy-image--loaded' : 'lazy-image--loading'} ${className}`}
      loading="lazy"
      {...props}
    />
  );
};

export default LazyImage;
